import { consola } from 'consola'
import { useTabsSync, TabSyncEventType } from '@shared/lib'

// Logger
const logger = consola.withTag('tabs-sync-login-plugin')

/**
 * Плагин для синхронизации авторизации между вкладками
 * 
 * ✅ Автоматически авторизует во всех вкладках одновременно
 * ✅ Предотвращает рассинхронизацию состояния авторизации
 * ✅ Немедленная реакция на авторизацию в любой вкладке
 */
export default defineNuxtPlugin((_nuxtApp) => {
  if (!process.client) return

  logger.info('🔌 Инициализация плагина синхронизации авторизации')

  const tabsSync = useTabsSync()

  // Слушаем событие авторизации из других вкладок
  tabsSync.on(TabSyncEventType.USER_LOGGED_IN, async (event) => {
    logger.info('🔐 Получено событие авторизации из другой вкладки', {
      fromTab: event.tabId,
      userId: event.data.userId,
      sessionId: event.data.sessionId,
      isRegistration: event.data.isRegistration || false,
    })

    // Импортируем сервисы динамически
    const { useSessionManager } = await import('~/entities/auth')
    const { useAuthStore } = await import('~/features/auth/model')
    const { useUserProfileStore } = await import('@entities/user')

    const sessionManager = useSessionManager()
    useAuthStore()
    const userProfileStore = useUserProfileStore()

    // Проверяем, уже авторизованы ли мы в этой вкладке
    if (sessionManager.hasActiveSession.value && sessionManager.authSessionId.value === event.data.sessionId) {
      logger.info('✅ Уже авторизованы с этим же sessionId, пропускаем')
      return
    }

    // Если авторизованы с другим sessionId - это новая сессия
    if (sessionManager.hasActiveSession.value && sessionManager.authSessionId.value !== event.data.sessionId) {
      logger.info('🔄 Обнаружена новая сессия, обновляем данные')
    }

    try {
      // Пробуем загрузить данные пользователя
      // Токены уже должны быть в cookies (HttpOnly), просто обновляем sessionId
      logger.info('🔄 Синхронизация сессии с другой вкладкой')
      
      // Обновляем sessionId в sessionStorage
      if (event.data.sessionId) {
        sessionStorage.setItem('authSessionId', event.data.sessionId)
        logger.success('✅ SessionId синхронизирован')
      }
      
      // Загружаем данные пользователя
      const success = await userProfileStore.fetchUser()
      
      if (success) {
        logger.success('✅ Данные пользователя загружены, вкладка синхронизирована')
        
        // Запускаем фоновое обновление токенов если еще не запущено
        const { useTokenRefreshV2 } = await import('~/features/auth/model')
        const tokenRefreshV2 = useTokenRefreshV2()
        tokenRefreshV2.startBackgroundRefresh()
      } else {
        logger.warn('⚠️ Не удалось загрузить данные пользователя')
      }
    } catch (error) {
      logger.error('❌ Ошибка синхронизации авторизации:', error)
    }
  })

  // Слушаем событие обновления сессии
  tabsSync.on(TabSyncEventType.SESSION_UPDATED, async (event) => {
    logger.info('🔄 Получено событие обновления сессии', {
      fromTab: event.tabId,
      sessionId: event.data.sessionId,
    })

    const { useSessionManager } = await import('~/entities/auth')
    const sessionManager = useSessionManager()

    // Обновляем sessionId если изменился
    if (event.data.sessionId && sessionManager.authSessionId.value !== event.data.sessionId) {
      sessionStorage.setItem('authSessionId', event.data.sessionId)
      logger.success('✅ SessionId обновлен')
    }
  })

  logger.success('✅ Плагин синхронизации авторизации инициализирован')
})

